/* eslint-disable react-hooks/rules-of-hooks */
import { defaultSchema } from "@atlaskit/adf-schema/schema-default";
import { JSONTransformer } from "@atlaskit/editor-json-transformer";
import { BitbucketTransformer } from "@atlaskit/editor-bitbucket-transformer";
import { setUserJiraDetails, setUserOpDetails } from "../store/userSlice";
import { Dispatch, UnknownAction } from "@reduxjs/toolkit";
import { IJiraAttachment } from "../common/interface";
import { jiraApi } from "../services/jira.service";
import { setProjectType } from "../store/projectSlice";


const adfTransformer = new JSONTransformer();
const bitbucketTransformer = new BitbucketTransformer(defaultSchema as any);

// Function to convert HTML to ADF using Jira transformer
const htmlToJira = (html: string) => {

  const adfText = bitbucketTransformer.parse(html);
  return adfTransformer.encode(adfText);

};

// Function to return the JSON stringified version of ADF content
export const convertHTMLToADF = (text: string) => {
  return htmlToJira(text);
};


type Payload = {
  userDetails: any;
  rawData: any;
  access_token: string;
  refresh_token: string;
};

declare global {
  interface Window {
    [key: string]: any; // You can refine this type if needed
  }
}

const setPayloadInLocal = (
  payload: Payload,
  service: 'openProject' | 'jira',
  refreshTokenFunction: () => Promise<any>,
  dispatch: Dispatch<UnknownAction>
) => {
  console.log(`[TESTING] Setting payload in local for ${service}`);


  if (!payload || !payload.rawData || !payload.access_token || !payload.refresh_token || !payload.userDetails) {
    console.error("Invalid payload structure");
    return;
  }


  console.log(`[TESTING] Setting local storage with payload`);

  localStorage.setItem(`${service}LoginDetails`, JSON.stringify(payload.rawData));
  localStorage.setItem(`${service}AccessToken`, JSON.stringify(payload.rawData.access_token));
  localStorage.setItem(`${service}RefreshToken`, JSON.stringify(payload.rawData.refresh_token));

  if (service === 'openProject') {
    dispatch(setUserOpDetails(payload.rawData.userDetails));
    dispatch(setProjectType("WayMore Project"));
  }
  if (service === 'jira') {
    dispatch(setUserJiraDetails(payload.rawData.userDetails));
    dispatch(setProjectType("Jira"));
  }

  console.log(`[TESTING] ${service} token set in localStorage`);

  if (window[`${service}RefreshTokenWorker`]) {
    clearInterval(window[`${service}RefreshTokenWorker`]);
  }

  window[`${service}RefreshTokenWorker`] = setInterval(async () => {
    console.log(`[TESTING] Refreshing ${service} tokens...`);
    const refreshResponse = await refreshTokenFunction();
    if (refreshResponse) {
      console.log(`[TESTING] ${service} tokens refreshed successfully`);
    } else {
      console.error(`[TESTING] Failed to refresh ${service} tokens`);
    }
  }, 1000 * 60 * 55) as NodeJS.Timer;
};

const getUserLoginData = (
  authCode: string,
  service: 'openProject' | 'jira',
  tokenFunction: (authCode: string) => Promise<any>,
  setLoggedInUser: (userName: string) => void,
  refreshTokenFunction: () => Promise<any>,
  dispatch: Dispatch<UnknownAction>
) => {
  console.log(`[TESTING] ${service} getUserLoginData called`);

  tokenFunction(authCode)
    .then((response: any) => {
      console.log(`[TESTING] ${service} getToken response`, response);
      const { refresh_token, access_token, userDetails } = response.data;
      if (refresh_token && access_token && userDetails) {
        const userName = service === 'openProject' ? userDetails.name : userDetails.displayName;
        setLoggedInUser(userName);
        const payload = { userDetails, rawData: response.data, access_token, refresh_token };
        setPayloadInLocal(payload, service, refreshTokenFunction, dispatch);
      } else {
        console.error(`[TESTING] ${service} OAuth failed`);
      }
    })
    .catch((error: any) => {
      console.error(`[TESTING] ${service} getUserLoginData failed`, error);
    });
};

export const listenToAuthCodeChanges = (
  service: 'openProject' | 'jira',
  uniqueId: string,
  tokenFunction: (authCode: string) => Promise<any>,
  setLoggedInUser: (userName: string) => void,
  refreshTokenFunction: () => Promise<any>,
  dispatch: Dispatch<UnknownAction>
) => {
  console.log(`[TESTING] Listening to ${service} auth code changes`);

  window.addEventListener('storage', (event) => {
    if (event.newValue) {
      console.log(`[TESTING] ${service} storage listener triggered`);
      const uniqueIdStorage = localStorage.getItem(uniqueId) ?? '';
      console.log(`[TESTING] State from ${service} localStorage with id ${uniqueId}: `, uniqueIdStorage);

      if (uniqueIdStorage && uniqueIdStorage !== 'null') {
        getUserLoginData(uniqueIdStorage, service, tokenFunction, setLoggedInUser, refreshTokenFunction, dispatch);
        localStorage.setItem(uniqueId, 'null');
      }
    }
  });
};

export const downloadJiraFile = async (attachment: IJiraAttachment) => {
  const fileUrl = attachment?.content;
  const filename = attachment?.filename;
  if (!fileUrl) {
    return;
  }

  try {
    const response = await jiraApi.get(fileUrl, { responseType: "blob" });

    const fileBlob = new Blob([response.data], { type: attachment.mimeType });
    const fileObjectURL = URL.createObjectURL(fileBlob);

    if (typeof window !== "undefined" && window.document) {
      // For web app
      const link = document.createElement("a");
      link.href = fileObjectURL;
      link.download = filename;
      link.click();
      // Clean up object URL
      URL.revokeObjectURL(fileObjectURL);
    } else {
      // For Electron app
    }
  } catch (error) {
    console.error("Error downloading file:", error);
  }
};
